
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, BarChart3, TrendingUp, TrendingDown, MoveHorizontal, Minus, Plus, Search, Maximize, LayoutGrid, Box, RotateCcw, ChevronLeft, ChevronRight, Coins } from 'lucide-react';

interface HistogramModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  theme: 'dark' | 'light';
  icons?: Record<string, string>;
  assetType?: 'crypto' | 'stocks';
}

type MetricType = 'roi' | 'profit' | 'cost' | 'netCost' | 'liq';
type GroupingType = 'asset' | 'category';

const HistogramModal: React.FC<HistogramModalProps> = ({ isOpen, onClose, data, theme, icons = {}, assetType = 'crypto' }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState<number>(0); 
  const [metric, setMetric] = useState<MetricType>('roi');
  const [grouping, setGrouping] = useState<GroupingType>('asset');
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const metricsScrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (assetType === 'stocks') {
        setGrouping('asset');
    }
  }, [assetType, isOpen]);

  useEffect(() => {
    if (grouping === 'category') {
      setZoom(1.0);
    } else {
      setZoom(0);
    }
  }, [grouping, isOpen]);

  const BASE_BAR_WIDTH = 48;
  const BASE_GAP = 32;
  const padding = { top: 60, right: 40, bottom: 120, left: 75 };

  const METRICS_CONFIG: Record<MetricType, { label: string, key: string, format: (v: number) => string }> = {
    roi: { label: '% GANANCIA', key: 'roi', format: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%` },
    profit: { label: '$ GANANCIA', key: 'totalProfit', format: (v) => `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
    cost: { label: 'INVERTIDO', key: 'totalBuy', format: (v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
    netCost: { label: 'INV - VEN', key: 'netCost', format: (v) => `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
    liq: { label: '% RECUPERADO', key: 'ventasPerc', format: (v) => `${v.toFixed(1)}%` }
  };

  const processedData = useMemo(() => {
    let baseData = data.map(d => ({
        ...d,
        netCost: (d.totalBuy || 0) - (d.totalSell || 0)
    }));

    if (grouping === 'category' && assetType === 'crypto') {
      const groups = {
        BTC: { assets: [] as any[], label: 'BTC', color: '#f7931a' },
        ETH: { assets: [] as any[], label: 'ETH', color: '#627eea' },
        Alts: { assets: [] as any[], label: 'CRIPTO', color: '#0ea5e9' }
      };

      baseData.forEach(d => {
        const ticker = d.ticket.split(/[-/]/)[0].toUpperCase();
        if (ticker === 'BTC') groups.BTC.assets.push(d);
        else if (ticker === 'ETH') groups.ETH.assets.push(d);
        else groups.Alts.assets.push(d);
      });

      baseData = Object.values(groups)
        .filter(g => g.assets.length > 0)
        .map(g => {
            const totalProfit = g.assets.reduce((sum, a) => sum + (a.totalProfit || 0), 0);
            const totalBuy = g.assets.reduce((sum, a) => sum + (a.totalBuy || 0), 0);
            const currentVal = g.assets.reduce((sum, a) => sum + (a.currentVal || 0), 0);
            const totalSell = g.assets.reduce((sum, a) => sum + (a.totalSell || 0), 0);
            
            return {
                ticket: g.label,
                symbolName: g.label === 'CRIPTO' ? 'Otras Criptomonedas' : g.label,
                totalProfit,
                totalBuy,
                currentVal,
                totalSell,
                netCost: totalBuy - totalSell,
                roi: totalBuy > 0 ? (totalProfit / totalBuy) * 100 : 0,
                ventasPerc: totalBuy > 0 ? (totalSell / totalBuy) * 100 : 0,
                isCategory: true,
                categoryColor: g.color
            };
        });
    }

    const key = METRICS_CONFIG[metric].key;
    return baseData.sort((a, b) => b[key] - a[key]);
  }, [data, metric, grouping, assetType]);

  useEffect(() => {
    if (!isOpen || !scrollContainerRef.current) return;
    const observer = new ResizeObserver(entries => {
        if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(scrollContainerRef.current);
    return () => observer.disconnect();
  }, [isOpen]);

  const fitZoom = useMemo(() => {
    if (containerWidth === 0 || processedData.length === 0) return 1;
    const availableWidth = containerWidth - padding.left - padding.right;
    const totalBaseWidth = processedData.length * (BASE_BAR_WIDTH + BASE_GAP);
    return Math.max(0.1, (availableWidth / totalBaseWidth) * 0.95); 
  }, [containerWidth, processedData.length]);

  if (!isOpen) return null;

  const getIconUrl = (symbol: string) => {
    const base = symbol.split(/[-/]/)[0].toUpperCase();
    return icons[base] || `https://assets.coincap.io/assets/icons/${base.toLowerCase()}@2x.png`;
  };

  const effectiveZoom = zoom === 0 ? fitZoom : zoom;
  const barWidth = Math.max(4, BASE_BAR_WIDTH * effectiveZoom); 
  const gap = Math.max(2, BASE_GAP * effectiveZoom);
  const chartHeight = 580; 
  
  const totalContentWidthWithoutPadding = processedData.length * (barWidth + gap) - gap;
  const availableCanvasWidth = containerWidth - padding.left - padding.right;
  const offsetX = (grouping === 'category' && totalContentWidthWithoutPadding < availableCanvasWidth) 
    ? (availableCanvasWidth - totalContentWidthWithoutPadding) / 2 
    : 0;

  const contentWidth = padding.left + padding.right + totalContentWidthWithoutPadding + (offsetX * 2);
  const chartWidth = Math.max(containerWidth, contentWidth);

  const currentKey = METRICS_CONFIG[metric].key;
  const values = processedData.map(d => d[currentKey]);
  const maxValue = Math.max(...values, 5);
  const minValue = Math.min(...values, -5);
  
  const isBalanced = metric === 'roi' || metric === 'profit' || metric === 'netCost';
  const absMax = Math.max(Math.abs(maxValue), Math.abs(minValue));
  const scaleTop = isBalanced ? absMax * 1.2 : maxValue * 1.2;
  const scaleBottom = isBalanced ? -absMax * 1.2 : Math.min(0, minValue);

  const totalRange = scaleTop - scaleBottom;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const getY = (val: number) => {
    if (totalRange === 0) return padding.top + plotHeight;
    const ratio = (val - scaleBottom) / totalRange;
    return (padding.top + plotHeight) - (ratio * plotHeight);
  };
  const zeroY = getY(0);

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/90 backdrop-blur-md z-[110] flex items-center justify-center p-4 animate-in fade-in duration-300",
    container: `${theme === 'dark' ? 'bg-[#0b0c10] border-[#20232a]' : 'bg-white border-slate-200'} border w-full max-w-[1450px] max-h-[92vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200`,
    header: `flex flex-col lg:flex-row lg:items-center justify-between px-8 py-5 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} gap-6`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-500' : 'text-slate-400',
    controlGroup: theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200',
    tooltip: theme === 'dark' ? 'bg-[#1a1c22]/95 border-[#363a45] text-white' : 'bg-white border-slate-200 text-slate-900',
    axisText: theme === 'dark' ? 'fill-slate-500' : 'fill-slate-600',
  };

  return (
    <div className={themeClasses.overlay} onClick={onClose}>
      <div className={themeClasses.container} onClick={e => e.stopPropagation()}>
        <div className={themeClasses.header}>
          <div className="flex items-center space-x-6 flex-shrink-0">
            <div className="flex items-center space-x-3">
                <div className="bg-indigo-600 p-2 rounded-xl shadow-lg shadow-indigo-600/20">
                    <BarChart3 size={18} className="text-white" />
                </div>
                <div className="hidden sm:block">
                    <h2 className={`text-base font-black ${themeClasses.textMain} tracking-tight uppercase`}>Histograma</h2>
                    <p className={themeClasses.textMuted + " text-[8px] font-bold uppercase tracking-widest"}>Rendimiento Consolidado</p>
                </div>
            </div>

            {assetType === 'crypto' && (
                <div className={`flex items-center p-1 rounded-xl border ${themeClasses.controlGroup}`}>
                    <button onClick={() => setGrouping('asset')} className={`flex items-center space-x-1.5 px-3 py-1.5 text-[9px] font-black uppercase rounded-lg transition-all ${grouping === 'asset' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}><LayoutGrid size={12} /><span>Activos</span></button>
                    <button onClick={() => setGrouping('category')} className={`flex items-center space-x-1.5 px-3 py-1.5 text-[9px] font-black uppercase rounded-lg transition-all ${grouping === 'category' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}><Box size={12} /><span>Categoría</span></button>
                </div>
            )}
          </div>

          <div className="flex-1 min-w-0 relative group">
            <div className={`absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r ${theme === 'dark' ? 'from-[#0b0c10]' : 'from-white'} to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`}></div>
            <div 
                ref={metricsScrollRef}
                className={`flex items-center space-x-1 p-1 rounded-xl border ${themeClasses.controlGroup} overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden scroll-smooth`}
            >
                {(Object.keys(METRICS_CONFIG) as MetricType[]).map((m) => (
                    <button 
                        key={m} 
                        onClick={() => setMetric(m)} 
                        className={`whitespace-nowrap px-4 py-2 text-[9px] font-black uppercase rounded-lg transition-all flex-shrink-0 ${metric === m ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                    >
                        {METRICS_CONFIG[m].label}
                    </button>
                ))}
            </div>
            <div className={`absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l ${theme === 'dark' ? 'from-[#0b0c10]' : 'from-white'} to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`}></div>
          </div>

          <div className="flex items-center space-x-4 flex-shrink-0">
            <div className={`hidden md:flex items-center space-x-3 px-3 py-1.5 rounded-xl border ${themeClasses.controlGroup}`}>
                <Search size={14} className="text-slate-500" />
                <div className="flex items-center space-x-2">
                    <button onClick={() => setZoom(z => Math.max(0.1, (z || fitZoom) - 0.1))} className="text-slate-500 hover:text-indigo-500 transition-colors"><Minus size={12} /></button>
                    <input type="range" min="0.1" max="2.0" step="0.01" value={effectiveZoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-20 h-1 bg-slate-500/20 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                    <button onClick={() => setZoom(z => Math.min(2.0, (z || fitZoom) + 0.1))} className="text-slate-500 hover:text-indigo-500 transition-colors"><Plus size={12} /></button>
                </div>
                <button onClick={() => setZoom(0)} className="text-slate-500 hover:text-indigo-500 ml-1" title="Ajustar Vista"><RotateCcw size={12} /></button>
                <span className="text-[9px] font-mono font-bold text-indigo-400 w-10 text-right">{Math.round(effectiveZoom * 100)}%</span>
            </div>
            <button onClick={onClose} className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'} transition-colors`}><X size={24} className={themeClasses.textMain} /></button>
          </div>
        </div>

        <div ref={scrollContainerRef} className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar relative bg-gradient-to-b from-transparent to-black/5">
          <div style={{ width: chartWidth, height: chartHeight }} className="relative">
            <svg width={chartWidth} height={chartHeight} className="block overflow-visible">
                {[0.25, 0.5, 0.75, 1.0].map(tick => {
                    const valPos = scaleTop * tick;
                    const valNeg = scaleBottom * tick;
                    const yPos = getY(valPos);
                    const yNeg = getY(valNeg);
                    
                    return (
                        <React.Fragment key={tick}>
                            <line x1={padding.left} y1={yPos} x2={chartWidth - padding.right} y2={yPos} className="stroke-white/5" strokeDasharray="4 4" />
                            <text x={padding.left - 15} y={yPos + 4} textAnchor="end" className={`text-[10px] font-mono font-bold ${themeClasses.axisText}`}>{METRICS_CONFIG[metric].format(valPos)}</text>
                            
                            {isBalanced && (
                                <>
                                    <line x1={padding.left} y1={yNeg} x2={chartWidth - padding.right} y2={yNeg} className="stroke-white/5" strokeDasharray="4 4" />
                                    <text x={padding.left - 15} y={yNeg + 4} textAnchor="end" className={`text-[10px] font-mono font-bold ${themeClasses.axisText}`}>{METRICS_CONFIG[metric].format(valNeg)}</text>
                                </>
                            )}
                        </React.Fragment>
                    );
                })}

                <line x1={padding.left} y1={zeroY} x2={chartWidth - padding.right} y2={zeroY} className="stroke-slate-500/30" strokeWidth="1.5" />

                {processedData.map((d, i) => {
                    const value = d[currentKey];
                    const x = padding.left + offsetX + i * (barWidth + gap);
                    const isPositive = value >= 0;
                    const barHeight = Math.max(Math.abs(getY(value) - zeroY), 2);
                    const yStart = isPositive ? getY(value) : zeroY;
                    
                    let color;
                    if (d.isCategory) color = d.categoryColor;
                    else if (metric === 'cost' || metric === 'netCost') color = '#3b82f6';
                    else if (metric === 'liq') color = '#f59e0b';
                    else color = isPositive ? '#10b981' : '#f43f5e';

                    const isHovered = hoveredIndex === i;
                    const labelY = chartHeight - padding.bottom + 25;
                    const fontSize = Math.max(9, Math.min(11, barWidth * 0.45));
                    const iconSize = 22;

                    return (
                        <g key={`${d.ticket}-${i}`} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} className="cursor-pointer transition-all duration-300">
                            <rect x={x - gap/2} y={padding.top} width={barWidth + gap} height={plotHeight} fill="transparent" />
                            <rect x={x} y={yStart} width={barWidth} height={barHeight} rx={barWidth > 12 ? 6 : 2} fill={color} opacity={isHovered ? 1 : 0.8} filter={isHovered ? 'url(#glow)' : ''} className="transition-all duration-300" />
                            {barWidth > 22 && (
                                <text x={x + barWidth / 2} y={isPositive ? yStart - 12 : yStart + barHeight + 20} textAnchor="middle" className="font-black font-mono transition-all" fill={color} style={{ fontSize: `${fontSize}px` }}>{METRICS_CONFIG[metric].format(value)}</text>
                            )}
                            {barWidth > 15 && (
                                <g transform={`translate(${x + barWidth/2}, ${labelY})`}>
                                    <foreignObject x={-iconSize/2} y={-5} width={iconSize} height={iconSize}>
                                        <div className={`w-full h-full rounded-full flex items-center justify-center bg-indigo-500/10 text-indigo-400 font-black text-[8px] relative overflow-hidden border border-white/5`}>
                                            {d.isCategory && d.ticket === 'CRIPTO' ? (
                                                <Coins size={12} className="text-[#0ea5e9]" />
                                            ) : (
                                                <>
                                                    <span className="uppercase">{d.ticket.charAt(0)}</span>
                                                    <img 
                                                        src={getIconUrl(d.ticket)} 
                                                        className="absolute inset-0 w-full h-full object-contain" 
                                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                                        alt="" 
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </foreignObject>
                                    <text x={0} y={32} textAnchor="middle" className={`font-black uppercase tracking-widest transition-colors ${isHovered ? 'fill-indigo-500' : themeClasses.axisText}`} style={{ fontSize: `${fontSize - 1}px` }}>{d.ticket.split('-')[0]}</text>
                                </g>
                            )}
                        </g>
                    );
                })}
                <defs><filter id="glow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="4" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter></defs>
            </svg>

            {hoveredIndex !== null && processedData[hoveredIndex] && (
                <div className={`absolute pointer-events-none p-5 rounded-2xl border shadow-2xl backdrop-blur-xl flex flex-col space-y-3 z-50 animate-in fade-in zoom-in-95 duration-150 ${themeClasses.tooltip}`} style={{ left: padding.left + offsetX + hoveredIndex * (barWidth + gap) + barWidth / 2, top: zeroY + (processedData[hoveredIndex][currentKey] >= 0 ? 30 : -30), transform: `translateX(-50%) ${processedData[hoveredIndex][currentKey] >= 0 ? '' : 'translateY(-100%)'}` }}>
                    <div className="flex items-center space-x-3 border-b border-white/10 pb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-indigo-500/10 text-indigo-400 font-black text-[10px] relative overflow-hidden border border-white/5`}>
                            {processedData[hoveredIndex].isCategory && processedData[hoveredIndex].ticket === 'CRIPTO' ? (
                                <Coins size={12} className="text-[#0ea5e9]" />
                            ) : (
                                <>
                                    <span className="uppercase">{processedData[hoveredIndex].ticket.charAt(0)}</span>
                                    <img 
                                        src={getIconUrl(processedData[hoveredIndex].ticket)} 
                                        className="absolute inset-0 w-full h-full object-contain p-1" 
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                        alt="" 
                                    />
                                </>
                            )}
                        </div>
                         <span className="text-xs font-black uppercase tracking-widest">{processedData[hoveredIndex].symbolName}</span>
                    </div>
                    <div className="space-y-2 min-w-[170px]">
                        <div className="flex justify-between items-center text-[10px]"><span className="opacity-50 font-bold uppercase tracking-tighter">Inversión Bruta</span><span className="font-mono font-black">${processedData[hoveredIndex].totalBuy.toLocaleString()}</span></div>
                        <div className="flex justify-between items-center text-[10px]"><span className="opacity-50 font-bold uppercase tracking-tighter">Costo Neto</span><span className="font-mono font-black">${processedData[hoveredIndex].netCost.toLocaleString()}</span></div>
                        <div className="flex justify-between items-center text-[10px]"><span className="opacity-50 font-bold uppercase tracking-tighter">Valor Mercado</span><span className="font-mono font-black">${processedData[hoveredIndex].currentVal.toLocaleString()}</span></div>
                        <div className="pt-3 mt-1 border-t border-white/10 flex justify-between items-center">
                            <span className="text-[9px] font-black uppercase opacity-80">{METRICS_CONFIG[metric].label}</span>
                            <span className="font-mono font-black text-base" style={{ color: processedData[hoveredIndex].isCategory ? processedData[hoveredIndex].categoryColor : (processedData[hoveredIndex][currentKey] >= 0 ? '#10b981' : '#f43f5e') }}>{METRICS_CONFIG[metric].format(processedData[hoveredIndex][currentKey])}</span>
                        </div>
                    </div>
                </div>
            )}
          </div>
        </div>
        
        <div className={`px-8 py-4 ${theme === 'dark' ? 'bg-black/40' : 'bg-slate-50'} border-t ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-between items-center relative z-20`}>
            <div className="flex items-center space-x-10">
                <div className="flex items-center space-x-4 text-[9px] font-black uppercase tracking-widest text-slate-500">
                    <div className="flex items-center space-x-2"><div className="w-2.5 h-2.5 rounded bg-emerald-500"></div><span>Ganancia</span></div>
                    <div className="flex items-center space-x-2"><div className="w-2.5 h-2.5 rounded bg-rose-500"></div><span>Pérdida</span></div>
                </div>
                {grouping === 'category' && (
                    <div className="flex items-center space-x-8 border-l border-white/10 pl-10 text-[9px] font-black uppercase tracking-widest text-slate-500">
                        <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 rounded-full bg-[#f7931a]/20 border border-[#f7931a]/40 flex items-center justify-center overflow-hidden">
                                <img src={getIconUrl('BTC')} className="w-3 h-3 object-contain" alt="" />
                            </div>
                            <span>BTC</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 rounded-full bg-[#627eea]/20 border border-[#627eea]/40 flex items-center justify-center overflow-hidden">
                                <img src={getIconUrl('ETH')} className="w-3 h-3 object-contain" alt="" />
                            </div>
                            <span>ETH</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 rounded-full bg-[#0ea5e9]/20 border border-[#0ea5e9]/40 flex items-center justify-center overflow-hidden">
                                <Coins size={10} className="text-[#0ea5e9]" />
                            </div>
                            <span>Cripto</span>
                        </div>
                    </div>
                )}
            </div>
            <div className="flex items-center space-x-3 text-[9px] font-black text-slate-500 uppercase tracking-widest">
                <MoveHorizontal size={14} className="text-indigo-500 animate-pulse" />
                <span>Deslizar para ver más</span>
            </div>
        </div>
      </div>
    </div>
  );
};

export default HistogramModal;
